/**
 * Easter embroidery patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterEmbroideryPatternsPack: ToilePack = {
    slug: 'easter-embroidery-patterns',
    category: 'season',
    name: 'Easter embroidery patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'wreath', label: 'Tulip wreath', subject: 'A round wreath of tulips and leaves'},
        {key: 'bunny', label: 'Bunny in grass', subject: 'A bunny sitting in tall grass with daisies around it'},
        {key: 'eggs', label: 'Row of eggs', subject: 'A row of three patterned eggs in cups'},
    ],
    styles: [
        {key: 'simple', label: 'Simple lines', style: 'bold single-weight black outline drawing, large open shapes, clean closed line art', palette: 'Black ink only', framing: 'Centered on a pure white page with a white margin, the full page used'},
        {key: 'detailed', label: 'Detailed', style: 'intricate fine black line art, dense decorative detail, clean closed line art', palette: 'Black ink only', framing: 'Filling the page edge to edge inside a thin white margin'},
        {key: 'doodle', label: 'Doodle', style: 'hand-drawn marker doodle line art, loose confident strokes, clean closed line art', palette: 'Black ink only', framing: 'Scattered across the page with a white margin'},
    ],
    directions: ['a chick instead', 'as a hoop border', 'smaller for a bookmark'],
    directionHint: 'try: a chick instead',
    seo: {
        title: 'Easter embroidery: tulip wreath, bunny, eggs',
        metaDescription: 'Nine Easter embroidery transfer patterns — a tulip wreath, a bunny in grass, a row of eggs — in simple, detailed or doodle stitch guides.',
        h1: 'Easter embroidery patterns: tulip wreath, bunny and eggs',
        tagline: 'Nine transfer patterns, three spring motifs, three stitch-guide styles each — ready to trace onto fabric.',
        intro: 'An embroidery transfer pattern for spring: a round wreath of tulips, a bunny sitting in tall grass with daisies, a row of three patterned eggs in cups. Each is drawn as a bold simple outline, a detailed pattern with fine decorative guides, or a loose doodle-style line. Fabric-free black-on-white line art, ready to trace. Ask for a different motif, like a chick, and it redraws.',
        useCases: [
            'a transfer pattern for the embroidery hoop',
            'an Easter table-linen design',
            'a small spring gift for a fellow stitcher',
            "a beginner's first spring hoop project",
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each pattern is centered on a square page with a wide white margin, sized to trace onto standard hoop fabric.'},
            {q: 'Do I need special transfer paper?', a: 'Any iron-on transfer pencil or light-box tracing method works — the pattern is clean black line art on white, ready either way.'},
            {q: 'Can I get a different spring motif?', a: "Yes — that's what the direction field is for. Type a different motif, like a chick or a hoop border, and it redraws in the same three styles."},
        ],
    },
}
